import React from 'react';
import { Calculator, Route, Droplet, DollarSign } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета расхода топлива
 */
const QuickAdjustmentSidebar = ({
  formData,
  setFormData,
  errors,
  isCalculating,
  fuelTypes
}) => {
  // Обработчик изменения расстояния
  const handleDistanceChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      distance: newValue
    }));
  };
  
  // Обработчик изменения расхода топлива
  const handleConsumptionChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      fuelConsumption: newValue
    }));
  };
  
  // Обработчик изменения цены топлива
  const handleFuelPriceChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      fuelPrice: newValue
    }));
  };
  
  // Функция расчета
  const calculateFuel = () => {
    // Эмулируем нажатие на кнопку "Рассчитать" в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные значения расстояния для быстрого выбора
  const predefinedDistances = [
    { value: '10', label: '10 км' },
    { value: '100', label: '100 км' },
    { value: '500', label: '500 км' },
    { value: '1000', label: '1000 км' }
  ];
  
  // Предопределенные значения расхода топлива для быстрого выбора
  const predefinedConsumptions = [
    { value: '6', label: '6 л/100км' },
    { value: '8', label: '8 л/100км' },
    { value: '10', label: '10 л/100км' },
    { value: '12', label: '12 л/100км' }
  ];

  // Типы топлива для быстрого выбора
  const popularFuelTypes = fuelTypes.slice(0, 4); // Первые 4 типа топлива
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-blue-500" />
        Быстрый расчет
      </h3>
      
      <div className="space-y-5">
        {/* Расстояние */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="distanceSlider" className="text-gray-600 dark:text-gray-400 flex items-center">
              <Route className="w-4 h-4 mr-1 text-blue-500" />
              Расстояние
            </label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.distance} км</span>
          </div>
          <input
            type="range"
            id="distanceSlider"
            min="1"
            max="2000"
            step="1"
            value={formData.distance}
            onChange={handleDistanceChange}
            aria-label="Расстояние"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предопределенными расстояниями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedDistances.map(distance => (
              <button
                key={distance.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, distance: distance.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.distance === distance.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {distance.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Расход топлива */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="consumptionSlider" className="text-gray-600 dark:text-gray-400 flex items-center">
              <Droplet className="w-4 h-4 mr-1 text-blue-500" />
              Расход
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.fuelConsumption} л/100км
            </span>
          </div>
          <input
            type="range"
            id="consumptionSlider"
            min="1"
            max="20"
            step="0.5"
            value={formData.fuelConsumption}
            onChange={handleConsumptionChange}
            aria-label="Расход топлива"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предопределенными значениями расхода */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedConsumptions.map(consumption => (
              <button
                key={consumption.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, fuelConsumption: consumption.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.fuelConsumption === consumption.value 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {consumption.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Цена топлива */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="fuelPriceSlider" className="text-gray-600 dark:text-gray-400 flex items-center">
              <DollarSign className="w-4 h-4 mr-1 text-blue-500" />
              Цена топлива
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formatCurrency(parseFloat(formData.fuelPrice), 2)}
            </span>
          </div>
          <input
            type="range"
            id="fuelPriceSlider"
            min="20"
            max="80"
            step="0.5"
            value={formData.fuelPrice}
            onChange={handleFuelPriceChange}
            aria-label="Цена топлива"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Выбор типа топлива */}
          <div className="grid grid-cols-2 gap-1 mt-2">
            {popularFuelTypes.map(fuelType => (
              <button
                key={fuelType.id}
                type="button"
                onClick={() => {
                  setFormData(prev => ({ 
                    ...prev, 
                    fuelType: fuelType.id,
                    fuelPrice: fuelType.defaultPrice.toString()
                  }));
                }}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.fuelType === fuelType.id 
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {fuelType.name} ({fuelType.defaultPrice.toFixed(2)} ₽)
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор типа расчета */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">
              Тип расчета
            </label>
          </div>
          <div className="grid grid-cols-3 gap-1">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, calculationType: 'consumption' }))}
              className={`p-1.5 text-xs rounded border transition-colors
                ${formData.calculationType === 'consumption' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Расход
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, calculationType: 'distance' }))}
              className={`p-1.5 text-xs rounded border transition-colors
                ${formData.calculationType === 'distance' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Расстояние
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, calculationType: 'cost' }))}
              className={`p-1.5 text-xs rounded border transition-colors
                ${formData.calculationType === 'cost' 
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Стоимость
            </button>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateFuel}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;